// ═══════════════════════════════════════════════════════════════════════════
// ANNUAL SUMMARY TAB - UrbanQ v1.9.6
// Resumen anual tipo Excel con 12 meses y categorias expandibles
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    FiCalendar, FiRefreshCw, FiChevronRight, FiChevronDown,
    FiMaximize2, FiX, FiDownload
} from 'react-icons/fi';

export default function AnnualSummaryTab({ year, token, apiUrl, scope, onNotification }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [expandedView, setExpandedView] = useState(false);

    const fetchSummary = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${apiUrl}/api/financial/annual-summary?year=${year}&scope=${scope}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setData(result.data);
            }
        } catch (err) {
            console.error('Error fetching annual summary:', err);
            onNotification?.('Error al cargar resumen anual', 'error');
        } finally {
            setLoading(false);
        }
    }, [year, token, apiUrl, scope, onNotification]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        if (num === 0) return '-';
        return `$${num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    };

    const formatPercent = (value) => {
        return `${(parseFloat(value || 0) * 100).toFixed(1)}%`;
    };

    const toggleCategory = (categoryName) => {
        setExpandedCategories(prev =>
            prev.includes(categoryName)
                ? prev.filter(c => c !== categoryName)
                : [...prev, categoryName]
        );
    };

    const monthHeaders = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    if (loading) {
        return (
            <div className="urbanq-finanzas-loading">
                <div className="urbanq-finanzas-spinner"></div>
            </div>
        );
    }

    const { summary, categoryBreakdown, totals } = data || {};

    const renderTable = () => (
        <table className="urbanq-finanzas-table urbanq-finanzas-annual-table grid-lines sticky-first-col">
            <thead>
                <tr>
                    <th>Concepto</th>
                    {monthHeaders.map(m => (
                        <th key={m} className="numeric">{m}</th>
                    ))}
                    <th className="numeric" style={{ background: '#f0f4ff' }}>TOTAL</th>
                    <th className="numeric" style={{ background: '#faf5ff' }}>PROMEDIO</th>
                </tr>
            </thead>
            <tbody>
                {/* Fila de Ingresos */}
                <tr className="income-row">
                    <td className="row-label" style={{ color: '#48bb78' }}>Ingresos</td>
                    {summary?.map((m, idx) => (
                        <td key={idx} className="numeric">
                            {formatCurrency(m.income)}
                        </td>
                    ))}
                    <td className="numeric row-total" style={{ color: '#48bb78' }}>
                        {formatCurrency(totals?.income)}
                    </td>
                    <td className="numeric row-average" style={{ color: '#48bb78' }}>
                        {formatCurrency(totals?.avgIncome)}
                    </td>
                </tr>

                {/* Fila de Gastos */}
                <tr className="expense-row">
                    <td className="row-label" style={{ color: '#e53e3e' }}>Gastos</td>
                    {summary?.map((m, idx) => (
                        <td key={idx} className="numeric">
                            {formatCurrency(m.expense)}
                        </td>
                    ))}
                    <td className="numeric row-total" style={{ color: '#e53e3e' }}>
                        {formatCurrency(totals?.expense)}
                    </td>
                    <td className="numeric row-average" style={{ color: '#e53e3e' }}>
                        {formatCurrency(totals?.avgExpense)}
                    </td>
                </tr>

                {/* Fila de Ahorro */}
                <tr className="savings-row" style={{ fontWeight: '600' }}>
                    <td className="row-label">Ahorro</td>
                    {summary?.map((m, idx) => (
                        <td key={idx} className={`numeric ${m.savings >= 0 ? 'positive' : 'negative'}`}>
                            {formatCurrency(m.savings)}
                        </td>
                    ))}
                    <td className={`numeric row-total ${totals?.savings >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrency(totals?.savings)}
                    </td>
                    <td className="numeric row-average">
                        -
                    </td>
                </tr>

                {/* Fila de % Ahorro */}
                <tr>
                    <td className="row-label" style={{ color: '#718096' }}>% Ahorro</td>
                    {summary?.map((m, idx) => (
                        <td key={idx} className="numeric" style={{ color: '#718096', fontSize: '12px' }}>
                            {formatPercent(m.savingsRate)}
                        </td>
                    ))}
                    <td className="numeric row-total" style={{ color: '#718096' }}>
                        -
                    </td>
                    <td className="numeric row-average" style={{ color: '#718096' }}>
                        -
                    </td>
                </tr>

                {/* Separador */}
                <tr>
                    <td colSpan={15} style={{ height: '20px', background: '#f7fafc', borderTop: '2px solid #e2e8f0' }}></td>
                </tr>

                {/* Desglose por categorias */}
                <tr>
                    <td colSpan={15} style={{ fontWeight: '700', fontSize: '13px', color: '#2d3748', padding: '15px' }}>
                        Desglose de Gastos por Categoria
                    </td>
                </tr>

                {categoryBreakdown?.map((cat, catIdx) => {
                    const isExpanded = expandedCategories.includes(cat.category);
                    const catTotal = cat.months?.reduce((sum, val) => sum + val, 0) || cat.total || 0;
                    const catAvg = catTotal / 12;

                    return (
                        <React.Fragment key={catIdx}>
                            <tr
                                className={`urbanq-finanzas-expandable-row ${isExpanded ? 'expanded' : ''}`}
                                onClick={() => toggleCategory(cat.category)}
                                style={{ cursor: 'pointer' }}
                            >
                                <td className="row-label">
                                    <span className="urbanq-finanzas-expand-icon">
                                        {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                    </span>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '3px',
                                            background: cat.color,
                                            marginRight: '8px'
                                        }}
                                    ></span>
                                    {cat.category}
                                </td>
                                {cat.months?.map((val, idx) => (
                                    <td key={idx} className="numeric" style={{ color: '#e53e3e' }}>
                                        {formatCurrency(val)}
                                    </td>
                                ))}
                                <td className="numeric row-total" style={{ color: '#e53e3e' }}>
                                    {formatCurrency(catTotal)}
                                </td>
                                <td className="numeric row-average" style={{ color: '#e53e3e' }}>
                                    {formatCurrency(catAvg)}
                                </td>
                            </tr>

                            {/* Subcategorias (si expandido) */}
                            {isExpanded && cat.subcategories?.map((sub, subIdx) => (
                                <tr key={`${catIdx}-${subIdx}`} className="urbanq-finanzas-child-row">
                                    <td style={{ paddingLeft: '40px', color: '#718096', fontSize: '13px' }}>
                                        {sub.name}
                                    </td>
                                    {sub.months?.map((val, idx) => (
                                        <td key={idx} className="numeric" style={{ color: '#a0aec0', fontSize: '12px' }}>
                                            {formatCurrency(val)}
                                        </td>
                                    ))}
                                    <td className="numeric" style={{ color: '#a0aec0', fontSize: '12px' }}>
                                        {formatCurrency(sub.total)}
                                    </td>
                                    <td className="numeric" style={{ color: '#a0aec0', fontSize: '12px' }}>
                                        {formatCurrency(sub.total / 12)}
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );

    return (
        <div className="urbanq-finanzas-annual-tab">
            <div className="urbanq-finanzas-section">
                <div className="urbanq-finanzas-section-header">
                    <h2><FiCalendar /> Resumen Anual {year}</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            className="urbanq-finanzas-btn secondary"
                            onClick={() => setExpandedView(true)}
                        >
                            <FiMaximize2 /> Expandir
                        </button>
                        <button className="urbanq-finanzas-btn-icon" onClick={fetchSummary}>
                            <FiRefreshCw />
                        </button>
                    </div>
                </div>

                {/* Totales anuales */}
                {totals && (
                    <div className="urbanq-finanzas-kpi-grid" style={{ marginBottom: '25px' }}>
                        <div className="urbanq-finanzas-kpi-card green">
                            <div className="urbanq-finanzas-kpi-icon">
                                <span style={{ fontSize: '20px' }}>+</span>
                            </div>
                            <div className="urbanq-finanzas-kpi-content">
                                <span className="urbanq-finanzas-kpi-value">
                                    {formatCurrency(totals.income)}
                                </span>
                                <span className="urbanq-finanzas-kpi-label">Ingresos {year}</span>
                            </div>
                        </div>

                        <div className="urbanq-finanzas-kpi-card red">
                            <div className="urbanq-finanzas-kpi-icon">
                                <span style={{ fontSize: '20px' }}>-</span>
                            </div>
                            <div className="urbanq-finanzas-kpi-content">
                                <span className="urbanq-finanzas-kpi-value">
                                    {formatCurrency(totals.expense)}
                                </span>
                                <span className="urbanq-finanzas-kpi-label">Gastos {year}</span>
                            </div>
                        </div>

                        <div className="urbanq-finanzas-kpi-card blue">
                            <div className="urbanq-finanzas-kpi-icon">
                                <span style={{ fontSize: '20px' }}>=</span>
                            </div>
                            <div className="urbanq-finanzas-kpi-content">
                                <span className="urbanq-finanzas-kpi-value" style={{
                                    color: totals.savings >= 0 ? '#48bb78' : '#e53e3e'
                                }}>
                                    {formatCurrency(totals.savings)}
                                </span>
                                <span className="urbanq-finanzas-kpi-label">Balance {year}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabla de resumen */}
                <div className="urbanq-finanzas-table-wrapper">
                    {summary && summary.length > 0 ? (
                        renderTable()
                    ) : (
                        <div className="urbanq-finanzas-empty">
                            <FiCalendar className="urbanq-finanzas-empty-icon" />
                            <h3>No hay datos para este ano</h3>
                            <p>Registra ingresos y gastos para ver el resumen</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Vista expandida (overlay) */}
            {expandedView && (
                <div className="urbanq-finanzas-expanded-overlay" onClick={() => setExpandedView(false)}>
                    <div
                        className="urbanq-finanzas-expanded-content"
                        onClick={e => e.stopPropagation()}
                        style={{ width: '95vw', maxWidth: '1600px' }}
                    >
                        <div className="urbanq-finanzas-expanded-header">
                            <h3><FiCalendar /> Resumen Anual {year} - Vista Completa</h3>
                            <button
                                className="urbanq-finanzas-expanded-close"
                                onClick={() => setExpandedView(false)}
                            >
                                <FiX />
                            </button>
                        </div>
                        <div className="urbanq-finanzas-expanded-body">
                            <div className="urbanq-finanzas-table-wrapper">
                                {renderTable()}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
